import { API, api, asset } from "./api";

let enabled = true;
let volume = 1;
let recorded = {};
let playing = false;
const warmed = new Set();

// One shared element: mobile Safari only lets an element play without a tap once it has been unlocked by a tap.
const player = typeof window !== "undefined" ? new Audio() : null;
if (player) player.preload = "auto";
const SILENT = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";
let unlocked = false;

const ctx = typeof window !== "undefined" && window.AudioContext ? new window.AudioContext() : null;

if (typeof document !== "undefined") {
  const unlock = () => {
    if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
    if (unlocked || playing || !player || (player.src && player.src !== SILENT)) return;
    unlocked = true;
    player.src = SILENT;
    player.play().catch(() => { if (player.src === SILENT) unlocked = false; });
  };
  ["pointerdown", "touchend", "keydown"].forEach((e) => document.addEventListener(e, unlock, true));
}

// Family recordings (text -> timestamp) take priority over admin uploads and AI voice.
export const loadRecordings = () =>
  api.get("/recordings/texts").then(({ data }) => { recorded = data || {}; return recorded; }).catch(() => recorded);

export const setAudioPrefs = (prefs = {}) => {
  enabled = prefs.audio_enabled !== false;
  volume = prefs.volume ?? 1;
};

export const audioUrl = (text) => `${API}/audio?text=${encodeURIComponent(text)}`;

const srcFor = (text, url, ai, fresh) => {
  const mine = !ai && text && recorded[text];
  if (mine) return `${audioUrl(text)}&mine=${encodeURIComponent(mine)}`;
  if (fresh) return `${audioUrl(text)}&v=${Date.now()}`;
  return url ? asset(url) : audioUrl(text);
};

export const stopAudio = () => {
  if (!player) return;
  player.onended = null;
  player.onerror = null;
  player.pause();
  playing = false;
};

export const pauseAudio = () => player && player.pause();
export const resumeAudio = () => player && player.src && player.src !== SILENT && player.paused && !player.ended && player.play().catch(() => {});

let seq = 0;
// Plays a pre-generated pronunciation (family recording > custom upload > cached AI voice).
export const play = (text, url, { ai = false, fresh = false } = {}) =>
  new Promise((resolve) => {
    if (!enabled || !player || (!text && !url)) return resolve();
    stopAudio();
    const src = srcFor(text, url, ai, fresh);
    const my = ++seq;
    const done = () => { if (my === seq) playing = false; resolve(); };
    player.onended = done;
    player.onerror = done;
    if (player.src !== src) player.src = src;
    else player.currentTime = 0;
    player.volume = volume;
    playing = true;
    unlocked = true;
    player.play().catch(done);
  });

// Warms the HTTP cache so the shared player starts quickly.
export const preload = (texts) =>
  texts.filter(Boolean).forEach((t) => {
    const src = srcFor(t);
    if (warmed.has(src)) return;
    warmed.add(src);
    fetch(src, { credentials: "include" }).catch(() => warmed.delete(src));
  });

export const sfx = (kind) => {
  if (!ctx || !enabled) return;
  const notes = kind === "good" ? [660, 880, 1046] : kind === "bad" ? [300, 220] : [520];
  notes.forEach((f, i) => {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "triangle";
    o.frequency.value = f;
    g.gain.setValueAtTime(0.12 * volume, ctx.currentTime + i * 0.09);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.09 + 0.25);
    o.connect(g).connect(ctx.destination);
    o.start(ctx.currentTime + i * 0.09);
    o.stop(ctx.currentTime + i * 0.09 + 0.3);
  });
};
